import Edit from "@mui/icons-material/Edit";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Refresh from "@mui/icons-material/Refresh";
import Upload from "@mui/icons-material/Upload";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
} from "@mui/material";
import { GridApiRef } from "components/OmnisearchDataGrid";
import {
  ExtractionStatus,
  JobStatus,
  JobTask,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";
import { Row } from ".";
import { ChemicalFromSdsDialog } from "./ChemicalFromSdsDialog";
import { useRetryDocumentExtraction } from "./hooks/useRetryDocumentExtraction";

const nonTerminalJobStatuses = [
  JobStatus.Queued,
  JobStatus.Running,
  JobStatus.Provisioning,
  JobStatus.ProvisioningEcsTask,
  JobStatus.ShouldCancel,
  JobStatus.Cancelling,
];

export const DocumentsBulkActionsMenu = function ({
  documents,
  apiRef,
  hasDocumentAI,
  onReviewMetadata,
}: {
  documents: Row[];
  apiRef: GridApiRef;
  hasDocumentAI?: boolean;
  onReviewMetadata?: (documentIds: string[]) => void;
}) {
  const buttonId = "documents-bulk-actions-button";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [openDialog, setDialogOpen] = useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Filter documents that are eligible for chemical creation (no existing chemical, no running job)
  const documentsEligibleForChemicalCreation = useMemo(() => {
    return documents.filter((doc) => {
      // Skip if document already has a chemical
      if ((doc.chemicalDocument?.length ?? 0) > 0) {
        return false;
      }

      // Skip if document has a running SDS Parse job
      const hasAProcessingSDSJob =
        doc.job?.some(
          (job) =>
            job.task === JobTask.FileExtraction &&
            nonTerminalJobStatuses.includes(job.status)
        ) ?? false;

      return !hasAProcessingSDSJob;
    });
  }, [documents]);

  // Filter documents that can be retried (all except those that are pending/analyzing)
  const retryableDocuments = useMemo(() => {
    return documents.filter(
      (doc) =>
        doc.extractionStatus !== ExtractionStatus.Pending && !!doc.tenantId
    );
  }, [documents]);

  const {
    retryDocumentExtractions,
    result: { loading },
  } = useRetryDocumentExtraction({
    onSuccess: () => {
      handleClose();
    },
  });

  const handleRetryDocumentExtractions = useCallback(async () => {
    const tenantIds = new Set(retryableDocuments.map((doc) => doc.tenantId));
    if (tenantIds.size > 1) {
      throw new Error("Documents must belong to the same tenant");
    }
    const tenantId = tenantIds.values().next().value;
    if (!tenantId) {
      throw new Error("No tenant ID found");
    }
    const documentIds = retryableDocuments.map((doc) => doc.id);
    await retryDocumentExtractions(tenantId, documentIds, "minimizedInTray");
  }, [retryDocumentExtractions, retryableDocuments]);

  return (
    <>
      <Button
        id={buttonId}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        onClick={handleClick}
        endIcon={<ExpandMore />}
        disabled={documents.length === 0}
      >
        Bulk Actions
      </Button>
      <Menu
        id="add-chemical-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": buttonId,
        }}
      >
        <MenuList dense>
          {hasDocumentAI && onReviewMetadata && (
            <MenuItem
              key="edit-documents"
              onClick={() => {
                onReviewMetadata(documents.map((doc) => doc.id));
                handleClose();
              }}
              disabled={documents.length === 0}
            >
              <ListItemIcon>
                <Edit />
              </ListItemIcon>
              <ListItemText>Edit Details ({documents.length})</ListItemText>
            </MenuItem>
          )}
          {hasDocumentAI && retryableDocuments.length > 0 && (
            <MenuItem
              key="retry-extractions"
              onClick={async () => {
                await handleRetryDocumentExtractions();
              }}
              disabled={retryableDocuments.length === 0 || loading}
            >
              <ListItemIcon>
                <Refresh />
              </ListItemIcon>
              <ListItemText>
                Retry Extractions ({retryableDocuments.length})
              </ListItemText>
            </MenuItem>
          )}
          <MenuItem
            key="sds-upload"
            onClick={() => {
              setDialogOpen(true);
              handleClose();
            }}
            disabled={documentsEligibleForChemicalCreation.length === 0}
          >
            <ListItemIcon>
              <Upload />
            </ListItemIcon>
            <ListItemText>
              Create Chemicals from Documents (
              {documentsEligibleForChemicalCreation.length})
            </ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>
      {openDialog && (
        <ChemicalFromSdsDialog
          open={openDialog}
          onClose={() => setDialogOpen(false)}
          documents={documentsEligibleForChemicalCreation}
          apiRef={apiRef}
        />
      )}
    </>
  );
};
